import { useCallback, useMemo, useState } from 'react';

// Multi-select state for bulk-CRUD tables/grids. Tracks a set of numeric ids and
// derives select-all / indeterminate against the currently visible id list.
export function useSelection(visibleIds: number[]) {
  const [sel, setSel] = useState<Set<number>>(new Set());

  const toggle = useCallback((id: number) => {
    setSel((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSel(new Set()), []);

  const selectedVisible = visibleIds.filter((id) => sel.has(id));
  const allChecked = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
  const someChecked = selectedVisible.length > 0 && !allChecked;

  const toggleAll = useCallback(() => {
    setSel((s) => {
      const allOn = visibleIds.length > 0 && visibleIds.every((id) => s.has(id));
      if (allOn) {
        const next = new Set(s);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...s, ...visibleIds]);
    });
  }, [visibleIds]);

  // Only ids still visible under the current filters — never act on hidden rows.
  const ids = useMemo(() => selectedVisible, [selectedVisible]);

  return { sel, ids, has: (id: number) => sel.has(id), toggle, toggleAll, clear, allChecked, someChecked, count: ids.length };
}
